import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { RootState } from "store/rootReducer";
import { selectTxnIdId, useMapStore } from "stores/map";
import { arrayOrEmpty } from "utils";

import { getWellNamesFromFilterIdPaginated } from "api/filter";

import { WELL_LIST_PAGE_SIZE } from "components/well-list/constants";
import { UwidFormatT, WellInfoT } from "components/well-list/context";
import useGetWellsFromFilterId, {
  WELL_LIST_CACHE_TIME_MS
} from "components/well-list/hooks/useGetWellsFromFilterId";
import { formatWellIdentifier } from "components/well-list/utils/formatWellIdentifier";

/**
 * How the jump selector labels its wells: the well list's identifier formats,
 * plus the reported well name. Kept local rather than added to UwidFormatT,
 * which the well list panel's settings, hotkey cycle and copy all share.
 */
export type WellJumpFormat = UwidFormatT | "wellName";

/** react-query key root for the well names; the full key is [WELL_NAMES_QUERY_KEY, filterId, txnId]. */
export const WELL_NAMES_QUERY_KEY = "getWellNamesFromFilterId";

export interface WellOption {
  /** Unformatted UWI — what is sent to the jump, whatever the label shows. */
  readonly value: string;
  /** Rendered per the chosen format. */
  readonly label: string;
}

/**
 * The wells the jump selector can offer, read from the ACTIVE FILTER.
 *
 * Deliberately not `useWellList()`: WellListProvider is mounted only around the
 * midstream navigator, not the map toolbar, so that hook throws here. This uses
 * the context-free fetch hook with the same react-query key the well-list panel
 * uses, so when the panel has already loaded a filter the selector costs no
 * extra request.
 *
 * The list is therefore the filter's wells and nothing else. Jumping to a well
 * OUTSIDE the active filter is still supported — by pasting it, which goes to
 * filter-svc's WellListProcessor and never consults this list.
 *
 * Well names are not part of that list, so the "wellName" format fetches them
 * for the same page, and only once it is chosen. A well with no well name keeps
 * its entity name as its label.
 *
 * `panelOpen` gates that fetch and the option lists. The selector stays mounted
 * behind a closed panel and `enabled` latches, so without it every filter the
 * user applied rebuilt two 50k-element arrays per map widget, and a panel left
 * on "wellName" pulled 50k names, all for a list nobody could see. The query
 * keys ignore it, so reopening reads what is already cached.
 */
export function useFilterWellOptions(
  format: WellJumpFormat,
  enabled = true,
  panelOpen = true
) {
  // state.filter.filterId is { id, lastUpdated }, not a bare string.
  const activeFilterId = useSelector((state: RootState) => state.filter.filterId?.id);

  // `enabled` must be ONE-WAY — set once the list has been opened, never unset.
  // Gating it on "is the panel open right now" emptied the selector: the id is
  // part of the react-query key, so a closed moment swapped to a key with no
  // data, and the antd dropdown lives in a portal outside the popover's DOM.
  const filterId = enabled ? activeFilterId : "";
  // Wells only — the jump is EntityKind.Well throughout, so the facility branch
  // useWellsFromFilter carries has no counterpart here.
  const txnId = useMapStore(selectTxnIdId) ?? "";

  // Read from the query's own `data` rather than its onSuccess callback:
  // onSuccess fires on a fetch, so a cached filter — the common case, since
  // the well-list panel shares this key — would leave the selector empty.
  const noop = useCallback(() => undefined, []);
  const { data, isLoading } = useGetWellsFromFilterId(filterId, txnId, noop);

  const wantsWellNames = format === "wellName";
  const wellNames = useQuery(
    [WELL_NAMES_QUERY_KEY, filterId, txnId],
    // The same page the well list fetched, so every option it offers can be named.
    () => getWellNamesFromFilterIdPaginated(filterId, 0, WELL_LIST_PAGE_SIZE),
    {
      enabled: wantsWellNames && panelOpen && !!filterId && filterId !== EMPTY_FILTER_ID,
      // A filterId names one set of wells and is never reused.
      staleTime: Infinity,
      cacheTime: WELL_LIST_CACHE_TIME_MS
    }
  );

  // The callback and `data` both carry the AxiosResponse, so the list is one
  // level deeper than it looks: response.data.uwiList.
  const wells = useMemo<WellInfoT[]>(
    () =>
      panelOpen
        ? arrayOrEmpty<{ uniqueId: string; formattedId: string }>(
            data?.data?.uwiList
          ).map((well) => ({ id: well.uniqueId, label: well.formattedId }))
        : [],
    [data, panelOpen]
  );

  // Re-labelled, not re-fetched, on a format change — the identifier sent to the
  // jump is always the unformatted UWI. Well names repeat (a pad's "… 1H"), so a
  // named well also carries its entity name, or two options would read the same.
  const options = useMemo<WellOption[]>(
    () =>
      wells.map((well) => {
        if (format !== "wellName") {
          return { value: well.id, label: formatWellIdentifier(well, format) };
        }
        const wellName = wellNames.data?.[well.id];
        return {
          value: well.id,
          label: wellName ? `${wellName} (${well.label})` : well.label
        };
      }),
    [wells, format, wellNames.data]
  );

  return {
    options,
    isLoading: isLoading || (wantsWellNames && wellNames.isLoading),
    // The labels fall back to entity names, so the selector has to say why.
    wellNamesFailed: wantsWellNames && wellNames.isError
  };
}
